// 汎用ツール（Layer 2）。LLM から見えるツールはこの層だけで、リソースが増えても
// ツールは増えない（プロンプト常時占有をほぼ一定に保つ）。
// 詳細: docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3
//
// 【引数はフラットに保つ】小型モデルはネストした引数（filters: {...}）を組むのが
// 苦手なため、フィルタは resource と同じ階層に並べる（実測プローブで検証済みの形）。
import { z } from 'zod';
import { withAuditSource } from '@shared/utils';
import {
  getResource,
  RESOURCE_NAMES,
  CUSTOMER_STATUS,
  CUSTOMER_RANK,
} from '../resources';
import type { SecretaryTool } from './types';

// ── list_records ──────────────────────────────────────────────
const listRecordsSchema = z.object({
  resource: z.enum(RESOURCE_NAMES),
  keyword: z.string().optional(),
  month: z
    .string()
    .regex(/^\d{4}-\d{2}$/, '年月は YYYY-MM 形式で指定してください')
    .optional(),
  status: z.enum(CUSTOMER_STATUS).optional(),
  rank: z.enum(CUSTOMER_RANK).optional(),
});
type ListRecordsArgs = z.infer<typeof listRecordsSchema>;

export const listRecordsTool: SecretaryTool<ListRecordsArgs> = {
  definition: {
    type: 'function',
    function: {
      name: 'list_records',
      // ※ 説明文は短く保つこと。ツール定義は毎回プロンプトに含まれるため、
      //   CPU推論では1トークンでも処理時間に効く。
      description: '一覧取得。quote=見積/customer=顧客/order=受注/delivery=納品/invoice=請求/purchase_order=注文書/subcontract_order=外注/procurement_order=仕入。最大20件。',
      parameters: {
        type: 'object',
        properties: {
          resource: { type: 'string', enum: [...RESOURCE_NAMES], description: 'quote=見積/customer=顧客' },
          keyword: { type: 'string', description: '名称(部分一致)' },
          month: { type: 'string', description: '年月 YYYY-MM（例 2026-07）' },
          status: {
            type: 'string',
            enum: [...CUSTOMER_STATUS],
            description: 'active取引中/inactive休止/prospect見込み',
          },
          rank: {
            type: 'string',
            enum: [...CUSTOMER_RANK],
            description: 'ランク。SSSが最上位、Cが最下位',
          },
        },
        required: ['resource'],
      },
    },
  },
  schema: listRecordsSchema,
  async execute(args) {
    const { resource, ...filters } = args;
    return getResource(resource).list(filters);
  },
};

// ── get_record ────────────────────────────────────────────────
const getRecordSchema = z.object({
  resource: z.enum(RESOURCE_NAMES),
  code: z.string().min(1, '番号を指定してください'),
});
type GetRecordArgs = z.infer<typeof getRecordSchema>;

export const getRecordTool: SecretaryTool<GetRecordArgs> = {
  definition: {
    type: 'function',
    function: {
      name: 'get_record',
      description: '1件の詳細を取得。quote=見積番号(0001-01)か案件番号(0001) / customer=顧客コード。',
      parameters: {
        type: 'object',
        properties: {
          resource: { type: 'string', enum: [...RESOURCE_NAMES], description: 'quote=見積/customer=顧客' },
          code: { type: 'string', description: '見積番号 0001-01・案件番号 0001・顧客コード等' },
        },
        required: ['resource', 'code'],
      },
    },
  },
  schema: getRecordSchema,
  async execute(args) {
    return getResource(args.resource).get(args.code);
  },
};

// ══ 書込ツール（C-2）══════════════════════════════════════════
// registry 側で WRITE_ENABLED のときだけ登録される。
// 結果カード（チャットのリンク表示）は LLM の文章ではなく、
// これらのツールが返す実行結果からシステム側が描画する（設計書 §3.5「嘘が成立しない構造」）。

// ── describe_resource ─────────────────────────────────────────
const describeResourceSchema = z.object({ resource: z.enum(RESOURCE_NAMES) });
type DescribeResourceArgs = z.infer<typeof describeResourceSchema>;

export const describeResourceTool: SecretaryTool<DescribeResourceArgs> = {
  definition: {
    type: 'function',
    function: {
      name: 'describe_resource',
      description: '書き込める項目・型・必須を返す。create/update_record の前に必ず呼ぶこと。',
      parameters: {
        type: 'object',
        properties: {
          resource: { type: 'string', enum: [...RESOURCE_NAMES], description: 'quote=見積/customer=顧客' },
        },
        required: ['resource'],
      },
    },
  },
  schema: describeResourceSchema,
  async execute(args) {
    const resource = getResource(args.resource);
    if (!resource.write) {
      return { error: `${resource.label}（${resource.name}）は書込に対応していません` };
    }
    return { resource: resource.name, label: resource.label, fields: resource.write.fields };
  },
};

// ── create_record ─────────────────────────────────────────────
const createRecordSchema = z.object({
  resource: z.enum(RESOURCE_NAMES),
  data: z.record(z.unknown()),
});
type CreateRecordArgs = z.infer<typeof createRecordSchema>;

export const createRecordTool: SecretaryTool<CreateRecordArgs> = {
  definition: {
    type: 'function',
    function: {
      name: 'create_record',
      description: 'レコードを作成する。data は describe_resource で確認した項目のみ。',
      parameters: {
        type: 'object',
        properties: {
          resource: { type: 'string', enum: [...RESOURCE_NAMES], description: 'quote=見積/customer=顧客' },
          data: { type: 'object', description: 'describe_resource が返した項目のオブジェクト' },
        },
        required: ['resource', 'data'],
      },
    },
  },
  schema: createRecordSchema,
  async execute(args, ctx) {
    const resource = getResource(args.resource);
    const spec = resource.write;
    if (!spec) return { error: `${resource.label}（${resource.name}）は書込に対応していません` };
    if (!ctx.userId) return { error: 'ログイン情報を取得できません。再ログインしてください' };

    const parsed = spec.createSchema.safeParse(args.data);
    if (!parsed.success) {
      const reason = parsed.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join(' / ');
      // 使える項目を同梱する: 小型モデルが describe_resource を挟まず1往復で自己修正できる
      const fieldNames = spec.fields.map((f) => f.name).join(', ');
      return { error: `入力が不正です: ${reason}。使える項目: ${fieldNames}` };
    }

    const userId = ctx.userId;
    const created = await withAuditSource('ai-secretary', () =>
      spec.create(parsed.data as Record<string, unknown>, userId)
    );
    return {
      kind: 'create',
      resource: resource.name,
      label: resource.label,
      code: created.code,
      id: created.id,
      url: spec.link(created.id),
      message: `${resource.label} ${created.code} を作成しました（AIレビュー待ち）`,
    };
  },
};

// ── update_record ─────────────────────────────────────────────
const updateRecordSchema = z.object({
  resource: z.enum(RESOURCE_NAMES),
  code: z.string().min(1, '対象の番号（code）を指定する'),
  changes: z.record(z.unknown()),
});
type UpdateRecordArgs = z.infer<typeof updateRecordSchema>;

export const updateRecordTool: SecretaryTool<UpdateRecordArgs> = {
  definition: {
    type: 'function',
    function: {
      name: 'update_record',
      description: '既存レコードを更新する。changes は変更する項目だけ。',
      parameters: {
        type: 'object',
        properties: {
          resource: { type: 'string', enum: [...RESOURCE_NAMES], description: 'quote=見積/customer=顧客' },
          code: { type: 'string', description: '見積番号 0001-01 / 顧客コード等' },
          changes: { type: 'object', description: '変更する項目のみのオブジェクト' },
        },
        required: ['resource', 'code', 'changes'],
      },
    },
  },
  schema: updateRecordSchema,
  async execute(args, ctx) {
    const resource = getResource(args.resource);
    const spec = resource.write;
    if (!spec) return { error: `${resource.label}（${resource.name}）は書込に対応していません` };
    if (!ctx.userId) return { error: 'ログイン情報を取得できません。再ログインしてください' };

    const parsed = spec.updateSchema.safeParse(args.changes);
    if (!parsed.success) {
      const reason = parsed.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join(' / ');
      // 使える項目を同梱する: 小型モデルが describe_resource を挟まず1往復で自己修正できる
      const fieldNames = spec.fields.map((f) => f.name).join(', ');
      return { error: `入力が不正です: ${reason}。使える項目: ${fieldNames}` };
    }

    const userId = ctx.userId;
    const updated = await withAuditSource('ai-secretary', () =>
      spec.update(args.code, parsed.data as Record<string, unknown>, userId)
    );
    if (!updated) {
      return { error: `「${args.code}」に一致する${resource.label}が見つかりませんでした` };
    }
    return {
      kind: 'update',
      resource: resource.name,
      label: resource.label,
      code: updated.code,
      id: updated.id,
      url: spec.link(updated.id),
      message: `${resource.label} ${updated.code} を更新しました（AIレビュー待ち）`,
    };
  },
};
